import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import {
  FlexibleTableColumn,
  FlexibleTableDataSource,
  FlexibleTableError,
  FlexibleTableEvent,
  FlexibleTableRow,
} from '../types';
import { styles } from '../view/styles';
import { DOMEventDispatcher } from 'apps/webcomponents/src/utils/event.utils';

export class GzFlexibleTableWebComponent extends LitElement {
  @property({ type: Array, attribute: true, reflect: true })
  public dataSource: FlexibleTableDataSource<any>;

  static get styles() {
    return styles;
  }

  protected update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    this.dataSource.requestUpdate = this.requestUpdate.bind(this);
  }

  private getRows(dataSource: FlexibleTableDataSource<any>): TemplateResult {
    return html`
      ${dataSource.rows.map((rowConfig, rowIndex) => {
        const rowData = this.dataSource.data[rowIndex];

        return html`
          <div
            class="flexible-table__row ${this.dataSource.setRowClasses ? this.dataSource.setRowClasses(rowData) : ''}"
            @click=${event => this.rowClicked(event, rowIndex, rowData)}
            role="row"
          >
            ${this.getColumns(rowConfig)}
          </div>
          ${this.dataSource.additionalRowRenderer(rowData)}
        `;
      })}
    `;
  }

  private getColumns(rowConfig: FlexibleTableRow): Array<TemplateResult> {
    const { columns } = rowConfig;

    return columns.map(column => this.getCell(column));
  }

  private getCell(column: FlexibleTableColumn): TemplateResult {
    const { sideHeader, value, cellRenderer, flex } = column;
    const content = (cellRenderer && cellRenderer()) || value;

    if (content) {
      return this.getCellTemplate({ content, flex, sideHeader });
    }

    throw new Error(FlexibleTableError.VALUE_OR_CELL_RENDERER_MISSING);
  }

  private getCellTemplate({ content, flex, sideHeader }): TemplateResult {
    const classes = {
      'flexible-table__cell': true,
      'flexible-table__cell--header': sideHeader,
    };

    const styles = {
      flex,
    };

    return html` <div class=${classMap(classes)} style=${styleMap(styles)} role="cell">${content}</div> `;
  }

  private rowClicked(event: Event, rowIndex: number, rowData: any) {
    event.stopPropagation();
    DOMEventDispatcher.dispatchGlobalEvent(this, FlexibleTableEvent.ROW_CLICKED, { event, rowIndex, rowData });
  }

  render() {
    return html` <div class="flexible-table" role="table">${this.getRows(this.dataSource)}</div> `;
  }
}
